import { History, Plus, X } from "lucide-react";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { Thread } from "@/components/assistant-ui/thread";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { ThreadList } from "@/domains/ai/ai-chat/components/thread-list";
import { useAiThreadContext } from "@/domains/ai/ai-chat/contexts/ai-thread-context";
import { useSalonStore } from "@/lib/store/salon";

interface AISidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AISidebar: FC<AISidebarProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation("ai-chat");
  const [view, setView] = useState<"chat" | "history">("chat");
  const { currentSalon } = useSalonStore();
  const { currentThreadId, resetThread, loadThread } = useAiThreadContext();

  if (!isOpen) return null;

  const handleSelectThread = async (threadId: string) => {
    setView("chat");
    try {
      await loadThread(threadId);
    } catch (error) {
      console.error(`[AISidebar] Failed to load thread ${threadId}:`, error);
    }
  };

  const handleNewThread = () => {
    setView("chat");
    resetThread();
  };

  return (
    <div className="h-full flex flex-col px-2 py-2">
      {/* Gradient border wrapper - Apple Intelligence style */}
      <div className="h-full p-[2px] bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 animate-gradient-shift rounded-lg overflow-hidden">
        <div className="h-full bg-card flex flex-col border border-border/50 rounded-[10px] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <TooltipIconButton
                variant="ghost"
                tooltip={t("sidebar.close")}
                side="right"
                onClick={onClose}
                className="h-8 w-8"
              >
                <X className="w-4 h-4" />
              </TooltipIconButton>
              <h2 className="font-semibold text-lg">
                {view === "chat" ? t("sidebar.title") : t("sidebar.conversations")}
              </h2>
            </div>
            <div className="flex items-center gap-1">
              {/* Toggle view button */}
              <TooltipIconButton
                variant="ghost"
                tooltip={view === "chat" ? t("sidebar.viewHistory") : t("sidebar.currentChat")}
                side="left"
                onClick={() => setView(view === "chat" ? "history" : "chat")}
                className="h-8 w-8"
              >
                <History className="w-4 h-4" />
              </TooltipIconButton>
              {/* New conversation button */}
              <button
                type="button"
                onClick={handleNewThread}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                <span>{t("sidebar.new")}</span>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {view === "chat" ? (
              <Thread />
            ) : (
              currentSalon && (
                <ThreadList
                  salonId={currentSalon.id}
                  onSelectThread={handleSelectThread}
                  onNewThread={handleNewThread}
                  currentThreadId={currentThreadId || undefined}
                />
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
